import { createFileRoute, Link } from "@tanstack/react-router";
import { useState } from "react";
import { useServerFn } from "@tanstack/react-start";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { adminListProducts, setProductActive, deleteProduct } from "@/lib/products.functions";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Loader2, Plus, Trash2, Pencil, ExternalLink } from "lucide-react";
import { formatPrice } from "@/lib/shop";

export const Route = createFileRoute("/_authenticated/admin/products/")({
  component: ProductsPage,
});

function ProductsPage() {
  const list = useServerFn(adminListProducts);
  const toggle = useServerFn(setProductActive);
  const remove = useServerFn(deleteProduct);
  const qc = useQueryClient();
  const [search, setSearch] = useState("");
  const [pendingDelete, setPendingDelete] = useState<{ id: string; name: string } | null>(null);

  const { data, isPending, isError, error } = useQuery({
    queryKey: ["admin", "products"],
    queryFn: () => list(),
  });

  const toggleMut = useMutation({
    mutationFn: (v: { id: string; active: boolean }) => toggle({ data: v }),
    onSuccess: (_r, v) => {
      qc.invalidateQueries({ queryKey: ["admin", "products"] });
      qc.invalidateQueries({ queryKey: ["shop", "products"] });
      toast.success(v.active ? "Product published" : "Product deactivated");
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const deleteMut = useMutation({
    mutationFn: (id: string) => remove({ data: { id } }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin", "products"] });
      qc.invalidateQueries({ queryKey: ["shop", "products"] });
      setPendingDelete(null);
      toast.success("Product deleted");
    },
    onError: (e: Error) => toast.error(e.message),
  });

  if (isPending) {
    return <div className="flex h-64 items-center justify-center"><Loader2 className="h-6 w-6 animate-spin" /></div>;
  }
  if (isError) return <p className="text-sm text-destructive">Failed to load: {(error as Error).message}</p>;

  const q = search.trim().toLowerCase();
  const rows = (data as any[]).filter((p) =>
    !q || [p.name, p.sku, p.brand, p.category, p.slug].filter(Boolean).join(" ").toLowerCase().includes(q),
  );

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Products</h1>
          <p className="text-sm text-muted-foreground">Add, edit, publish and deactivate shop products.</p>
        </div>
        <Button asChild>
          <Link to="/admin/products/$id" params={{ id: "new" }}><Plus className="mr-2 h-4 w-4" />New product</Link>
        </Button>
      </div>

      <Card>
        <CardHeader className="gap-3">
          <CardTitle>Catalogue ({rows.length})</CardTitle>
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by name, SKU, brand or category"
            className="max-w-sm"
          />
        </CardHeader>
        <CardContent className="overflow-x-auto p-0">
          <table className="w-full min-w-[820px] text-sm">
            <thead className="bg-muted/50 text-left text-xs uppercase tracking-widest text-muted-foreground">
              <tr>
                <th className="px-4 py-3">Product</th>
                <th className="px-4 py-3">Category</th>
                <th className="px-4 py-3">Standard</th>
                <th className="px-4 py-3">Physio</th>
                <th className="px-4 py-3">Stock</th>
                <th className="px-4 py-3">Published</th>
                <th className="px-4 py-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 ? (
                <tr>
                  <td colSpan={7} className="px-4 py-10 text-center text-muted-foreground">
                    No products yet. Use “New product” to add your first item.
                  </td>
                </tr>
              ) : (
                rows.map((p) => (
                  <tr key={p.id} className="border-t align-middle">
                    <td className="px-4 py-3">
                      <div className="font-medium">{p.name}</div>
                      <div className="text-xs text-muted-foreground">
                        {p.brand ? `${p.brand} · ` : ""}{p.sku || "No SKU"} · /{p.slug}
                      </div>
                    </td>
                    <td className="px-4 py-3">{p.subcategory || p.category || "—"}</td>
                    <td className="px-4 py-3 whitespace-nowrap">{formatPrice(Number(p.standard_price))}</td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      {p.physio_price == null ? "—" : formatPrice(Number(p.physio_price))}
                    </td>
                    <td className="px-4 py-3">{p.stock_quantity}</td>
                    <td className="px-4 py-3">
                      <Switch
                        checked={p.active}
                        disabled={toggleMut.isPending}
                        onCheckedChange={(v) => toggleMut.mutate({ id: p.id, active: v })}
                        aria-label={`Publish ${p.name}`}
                      />
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center justify-end gap-1">
                        <Button asChild variant="ghost" size="sm">
                          <Link to="/admin/products/$id" params={{ id: p.id }}>
                            <Pencil className="mr-1 h-3.5 w-3.5" />Edit
                          </Link>
                        </Button>
                        {p.active && (
                          <Button asChild variant="ghost" size="sm">
                            <Link to="/shop/$slug" params={{ slug: p.slug }} target="_blank">
                              <ExternalLink className="h-3.5 w-3.5" />
                            </Link>
                          </Button>
                        )}
                        <Button
                          variant="ghost"
                          size="sm"
                          className="text-destructive hover:text-destructive"
                          onClick={() => setPendingDelete({ id: p.id, name: p.name })}
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </CardContent>
      </Card>

      <AlertDialog open={!!pendingDelete} onOpenChange={(o) => !o && setPendingDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete product</AlertDialogTitle>
            <AlertDialogDescription>
              This permanently removes “{pendingDelete?.name}” from the catalogue. To hide it temporarily, switch
              Published off instead.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Keep product</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => pendingDelete && deleteMut.mutate(pendingDelete.id)}
              disabled={deleteMut.isPending}
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
